/**
 * TanStack Query hooks over the generated client. One hook per endpoint the app uses — thin
 * wrappers, not an abstraction layer, so a component reading this can see exactly which
 * request it triggers.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from './client'
import type { Comment, IngredientName, Recipe, Tag, Unit, User } from './types'

async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>) {
  const { data, error, response } = await promise
  if (error) {
    const detail =
      typeof error === 'object' && error !== null && 'detail' in error
        ? String((error as { detail: unknown }).detail)
        : response.statusText
    throw new ApiError(detail, response.status, error)
  }
  return data as T
}

// ------------------------------------------------------------------------------------- auth

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const { data, response } = await api.GET('/api/auth/me/')
      if (response.status === 403 || response.status === 401) return null
      if (!data) throw new ApiError('Impossible de charger le profil.', response.status)
      return data
    },
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      unwrap<User>(api.POST('/api/auth/login/', { body })),
    onSuccess: (user) => queryClient.setQueryData(['me'], user),
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      invite_code: string
      email: string
      password: string
      display_name?: string
    }) => unwrap<User>(api.POST('/api/auth/register/', { body })),
    onSuccess: (user) => queryClient.setQueryData(['me'], user),
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/auth/logout/')),
    onSuccess: () => {
      queryClient.setQueryData(['me'], null)
      queryClient.clear()
    },
  })
}

// ---------------------------------------------------------------------------------- recipes

export interface RecipeFilters {
  search?: string
  tags?: string[]
}

export function useRecipes(filters: RecipeFilters) {
  return useQuery({
    queryKey: ['recipes', filters],
    queryFn: () =>
      unwrap(
        api.GET('/api/recipes/', {
          params: {
            query: {
              search: filters.search || undefined,
              tags: filters.tags?.length ? filters.tags.join(',') : undefined,
              ordering: '-created_at',
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  })
}

export function useRecipe(id: number) {
  return useQuery({
    queryKey: ['recipe', id],
    queryFn: () => unwrap<Recipe>(api.GET('/api/recipes/{id}/', { params: { path: { id } } })),
    enabled: Number.isFinite(id),
  })
}

/**
 * What the editor sends. The fields F-15 makes mandatory are non-optional here too, so the
 * form cannot omit one and discover it as a 400 — the serializer still enforces it, this is
 * just the compiler saying it first.
 */
export interface RecipeInput {
  title: string
  servings: number
  prep_time_min: number
  cook_time_min: number
  ingredients: {
    quantity: string | null
    unit: string | null
    name: string
    note?: string
    is_optional?: boolean
  }[]
  steps: { text: string }[]
  description?: string
  source?: string
  notes?: string
  tags?: string[]
}

/** A write can mint tags and catalog entries, so both vocabularies are refetched after one. */
function invalidateAfterWrite(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['recipes'] })
  void queryClient.invalidateQueries({ queryKey: ['tags'] })
  void queryClient.invalidateQueries({ queryKey: ['ingredient-names'] })
}

export function useCreateRecipe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: RecipeInput) =>
      unwrap<Recipe>(api.POST('/api/recipes/', { body: body as Recipe })),
    onSuccess: () => invalidateAfterWrite(queryClient),
  })
}

export function useUpdateRecipe(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: RecipeInput) =>
      unwrap<Recipe>(
        api.PATCH('/api/recipes/{id}/', { params: { path: { id } }, body: body as Recipe }),
      ),
    onSuccess: (recipe) => {
      queryClient.setQueryData(['recipe', id], recipe)
      invalidateAfterWrite(queryClient)
    },
  })
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(api.DELETE('/api/recipes/{id}/', { params: { path: { id } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recipes'] }),
  })
}

// ----------------------------------------------------------------------------------- photos

/**
 * A photo is its own request (F-20): a multipart file and a nested JSON recipe do not share a
 * body, and a recipe being created has no id to attach a file to until it exists. Both calls
 * return the whole recipe, so the cache is replaced rather than refetched.
 */
function adoptRecipe(queryClient: ReturnType<typeof useQueryClient>, recipe: Recipe) {
  queryClient.setQueryData(['recipe', recipe.id], recipe)
  // The card shows the photo too, so the grid is stale until this runs.
  void queryClient.invalidateQueries({ queryKey: ['recipes'] })
}

/**
 * The recipe id is a mutation argument here rather than a hook parameter, unlike every other
 * hook in this file: when the editor creates a recipe there is no id at render time, and the
 * upload happens moments after the POST that mints one.
 */
export function useUploadPhoto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      unwrap<Recipe>(
        api.POST('/api/recipes/{id}/photo/', {
          params: { path: { id } },
          // OpenAPI describes a binary upload as a `string`, so both of these casts are the
          // schema's type meeting the runtime's `File`. They are the only two in this file.
          body: { photo: file } as unknown as { photo: string },
          // openapi-fetch serialises to JSON by default. Multipart has to be a FormData so
          // the browser sets its own boundary header — which is why this is not the default.
          bodySerializer: (body) => {
            const form = new FormData()
            form.append('photo', body.photo as unknown as File)
            return form
          },
        }),
      ),
    onSuccess: (recipe) => adoptRecipe(queryClient, recipe),
  })
}

export function useDeletePhoto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      unwrap<Recipe>(api.DELETE('/api/recipes/{id}/photo/', { params: { path: { id } } })),
    onSuccess: (recipe) => adoptRecipe(queryClient, recipe),
  })
}

// --------------------------------------------------------------------------------- comments

/**
 * Writing to a thread changes the recipe's card as well as the thread (F-19), so both
 * mutations invalidate `['recipes']` too. Forgetting that is how a count goes stale in the
 * grid the moment you navigate back to it.
 */
function invalidateThread(queryClient: ReturnType<typeof useQueryClient>, recipeId: number) {
  void queryClient.invalidateQueries({ queryKey: ['comments', recipeId] })
  void queryClient.invalidateQueries({ queryKey: ['recipes'] })
}

export function useComments(recipeId: number) {
  return useQuery({
    queryKey: ['comments', recipeId],
    queryFn: () =>
      unwrap<Comment[]>(
        api.GET('/api/recipes/{recipe_id}/comments/', {
          params: { path: { recipe_id: recipeId } },
        }),
      ),
    enabled: Number.isFinite(recipeId),
  })
}

export function useCreateComment(recipeId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      unwrap<Comment>(
        api.POST('/api/recipes/{recipe_id}/comments/', {
          params: { path: { recipe_id: recipeId } },
          body: { body } as Comment,
        }),
      ),
    onSuccess: () => invalidateThread(queryClient, recipeId),
  })
}

export function useDeleteComment(recipeId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(api.DELETE('/api/comments/{id}/', { params: { path: { id } } })),
    onSuccess: () => invalidateThread(queryClient, recipeId),
  })
}

// -------------------------------------------------------------------------------- tags/units

export function useTags() {
  return useQuery({
    queryKey: ['tags'],
    queryFn: () => unwrap<Tag[]>(api.GET('/api/tags/')),
    staleTime: 60_000,
  })
}

export function useIngredientNames() {
  return useQuery({
    queryKey: ['ingredient-names'],
    queryFn: () => unwrap<IngredientName[]>(api.GET('/api/ingredient-names/')),
    staleTime: 60_000,
  })
}

export function useUnits() {
  return useQuery({
    queryKey: ['units'],
    queryFn: () => unwrap<Unit[]>(api.GET('/api/meta/units/')),
    staleTime: Infinity,
  })
}
